import { StackLayout } from '@leagueplatform/genesis-core';
import {
  type MasonryEngineNodeAction,
  type MasonryEngineActionHandler,
  type MasonryEngineNodeRendererProps,
  type MasonryEngineNode,
  useMasonryEngineActionEmitter,
} from '@leagueplatform/masonry-engine';
import { type MasonryDriverAction } from '@leagueplatform/masonry-renderers';
import { Pagination } from '@leagueplatform/genesis-core-alpha';
import { replaceSearchParam } from 'utils/replace-search-param.util';

// Masonry's web SDK ships no `paginationLayout` renderer; tenants must
// register one for the (nodeType, namespace) pair their YAML uses.
// Mirrors scan-web's implementation.

export const PAGINATION_NAMESPACE = 'scfhp_form_statuses';

type SetPageAction = MasonryEngineNodeAction<
  'setPage',
  {
    nodeId: string;
    page: number;
  }
>;

const getSetPageAction = (nodeId: string, page: number): SetPageAction => ({
  type: 'setPage',
  namespace: PAGINATION_NAMESPACE,
  payload: {
    nodeId,
    page,
  },
});

export const setPageActionHandler: MasonryEngineActionHandler<
  MasonryEngineNodeAction<'setPage', Record<string, any>>,
  MasonryDriverAction
> = ({ payload }, { messagingController }) => {
  messagingController.sendMessage(payload.nodeId as string, {
    type: 'reload',
    payload: {
      renderIntermediately: 'loader',
      setQueryParams: (current) => ({
        ...current,
        page: String(payload.page),
      }),
    },
  });
};

type PaginationLayoutNodeProperties = {
  currentPage: number;
  totalPages: number;
};

type PaginationLayoutNodeSections = {
  items: Array<MasonryEngineNode>;
  pageNumberButtons?: Array<MasonryEngineNode>;
};

type PaginationLayoutNode = MasonryEngineNode<
  'paginationLayout',
  PaginationLayoutNodeProperties,
  {},
  PaginationLayoutNodeSections
>;

type PaginationLayoutProps =
  MasonryEngineNodeRendererProps<PaginationLayoutNode>;

export const PaginationLayout = ({
  items,
  nodeId,
  currentPage,
  totalPages,
}: PaginationLayoutProps) => {
  const invokeAction = useMasonryEngineActionEmitter();

  const setSelectedPage = (selectedPage: number) => {
    replaceSearchParam('page', String(selectedPage));
    invokeAction(getSetPageAction(nodeId, selectedPage));
  };

  if (currentPage > totalPages && totalPages !== 0) setSelectedPage(totalPages);

  return (
    <StackLayout horizontalAlignment="stretch">
      {items}
      {totalPages > 1 && (
        <Pagination
          defaultPage={1}
          onPageChange={(selectedPage) =>
            setSelectedPage(selectedPage.pageNumber)
          }
          page={currentPage}
          totalPages={totalPages}
          css={{ justifyContent: 'center', marginBlockStart: '$two' }}
        />
      )}
    </StackLayout>
  );
};
